import type { ComponentType } from 'react'
import AssignmentSheet from './AssignmentSheet'
import DailyCoverageReport from './DailyCoverageReport'
import DailyRoster from './DailyRoster'
import DetailedOutReport from './DetailedOutReport'
import LeaveReport from './LeaveReport'
import ManpowerReport from './ManpowerReport'
import MonthlyManpowerReport from './MonthlyManpowerReport'
import OvertimeReport from './OvertimeReport'
import PayPeriodSchedule from './PayPeriodSchedule'
import SeniorityRoster from './SeniorityRoster'

export type ReportId = 'daily-coverage' | 'two-week-manpower' | 'monthly-manpower' | 'leave-availability' | 'detailed-out' | 'seniority-roster' | 'overtime-planning' | 'assignment-sheet' | 'daily-roster' | 'pay-period-schedule'

// The reports a role can open, in the order they are listed. Each role's
// page picks one by id and keeps it under that role's own route prefix.
export const REPORT_CATALOG: { id: ReportId; label: string; title: string; subtitle: string; about: string[]; Component: ComponentType }[] = [
  {
    id: 'daily-roster',
    label: 'Daily roster',
    title: 'Daily Roster',
    subtitle: 'Officers on duty for one date, by unit, shift, and title, with their assignment',
    about: [
      'The officers on duty on one date, one row each: unit, shift, title, name, and assignment, in that order. A unit is a team. Someone whose full-day leave is approved is not on duty.',
      'Choose the date, then narrow by unit, shift, or title. The PDF and Excel exports list the same rows, so the file can be saved each day.',
    ],
    Component: DailyRoster,
  },
  {
    id: 'pay-period-schedule',
    label: 'Pay period schedule',
    title: 'Pay Period Schedule',
    subtitle: 'Each officer\'s shift, days off, and leave by type for one pay period',
    about: [
      'Every officer\'s published schedule for the pay period, a day at a time, with their unit, shift, and title. Days off and leave show on the day they fall, and the totals count work days, days off, and days of each type of leave.',
      'A unit whose schedule is not yet published for the pay period is named above the table and has no rows.',
    ],
    Component: PayPeriodSchedule,
  },
  {
    id: 'daily-coverage',
    label: 'Daily coverage',
    title: 'Daily Coverage Report',
    subtitle: 'Staffing against target by date, shift, assignment, and team',
    about: [
      'Staffing against the targets, for each date, shift, assignment, and team. Each row compares the people available with the minimum the staffing target requires, and flags unfilled roles and missing qualifications.',
      'Headcount filled is the available headcount over the required headcount, with each assignment counted up to its own requirement. Summary gives one row per date; Detailed gives a row for each shift and assignment.',
    ],
    Component: DailyCoverageReport,
  },
  {
    id: 'two-week-manpower',
    label: 'Two-week manpower',
    title: 'Two-Week Manpower Report',
    subtitle: 'Manpower, availability, leave, detail, and planned overtime for each day of the pay period',
    about: [
      'The people on the schedule for each day of the pay period, and where they are: total manpower, those on duty, those available, those on leave or detailed out, and planned overtime hours.',
      'Available % is available people over total manpower. By shift gives the same figures for each shift. Coverage status is the most urgent status among that day\'s assignments.',
    ],
    Component: ManpowerReport,
  },
  {
    id: 'monthly-manpower',
    label: 'Monthly manpower',
    title: 'Monthly Manpower Report',
    subtitle: 'Manpower, availability, leave, detail, and planned overtime for each day of a month',
    about: [
      'The same figures as the two-week report for each day of a calendar month: total manpower, those on duty, those available, those on leave or detailed out, and planned overtime hours. The totals at the top cover the whole month.',
      'Choose the month, then narrow by team, supervisor, shift, or status. A month runs across pay periods, so the days shown can belong to more than one. By shift gives the same figures for each shift.',
    ],
    Component: MonthlyManpowerReport,
  },
  {
    id: 'leave-availability',
    label: 'Leave and availability',
    title: 'Leave and Availability Report',
    subtitle: 'Leave as requested or as scheduled, with its effect on coverage',
    about: [
      'Requested leave lists leave requests and their decisions: the state of each request, who decided and when, and whether it was approved, returned, or denied. Scheduled leave lists the leave in each team\'s latest published schedule.',
      'Coverage effect reads the team\'s coverage on the leave dates, naming the most urgent status and how many dates hold it.',
    ],
    Component: LeaveReport,
  },
  {
    id: 'detailed-out',
    label: 'Detailed out',
    title: 'Detailed-Out Report',
    subtitle: 'Employees on detail, with their dates, destination, and effect on available manpower',
    about: [
      'Each detail that touches the date range: the employee, the type of detail, the originating team and destination, the start and end dates, and its status today (active, upcoming, or ended).',
      'An employee on detail is taken out of available manpower for each day of the detail. Days in range counts the days of the detail that fall inside the dates chosen.',
    ],
    Component: DetailedOutReport,
  },
  {
    id: 'seniority-roster',
    label: 'Seniority roster',
    title: 'Seniority Roster',
    subtitle: 'Employees in seniority order, with team, title, shift, qualifications, and status',
    about: [
      'Everyone in scope, ordered by seniority date with the earliest first. Each row shows the team, title, shift, and the qualifications held today, and whether the employee is active or on detail.',
      'The seniority number is the position in the whole roster, so it stays the same when the list is filtered. The roster is as of today.',
    ],
    Component: SeniorityRoster,
  },
  {
    id: 'overtime-planning',
    label: 'Overtime planning',
    title: 'Overtime Planning Report',
    subtitle: 'Planned hours beyond the normal schedule, with the coverage gap they address',
    about: [
      'The overtime planned on the schedule: for each entry the employee, date, shift, assignment, planned hours, and the reason given, with the coverage gap on that date, shift, and assignment that the hours address.',
      'Totals by employee, team, and pay period show where the hours fall. Include detailed-out personnel adds or removes the people who are on detail that day. This is planning information.',
    ],
    Component: OvertimeReport,
  },
  {
    id: 'assignment-sheet',
    label: 'Daily assignment sheet',
    title: 'Daily Assignment Sheet',
    subtitle: 'The daily assignment board for one date, as a page to post or hand out',
    about: [
      'The people on duty for one team on one date, grouped by assignment in sort priority order, with each person\'s shift times and activity. Anyone on duty without an assignment is listed last.',
      'Someone whose full-day leave is approved is not on duty. Print gives the sheet alone, ready to post at the start of a shift. The PDF and Excel exports list the same people.',
    ],
    Component: AssignmentSheet,
  },
]
